import { Injectable, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { PortTariffLineUnitTypes, isCargoUnitType } from '../../../shared/enums/port-tariff-line-unit-types.enum';
import { PortTariffLineVesselProperties } from '../../../shared/enums/port-tariff-line-vessel-properties.enum';
import { PortTariffLineTypes } from '../../../shared/enums/port-tariff-line-types.enum';
import { PortTariffLineExpenseCategories } from '../../../shared/enums/port-tariff-line-expense-category.enum';
import { PortTariffLineOperationDirections } from '../../../shared/enums/port-tariff-line-operation-direction.enum';
import { PortTariffLineModel } from '../../../shared/models/port-tariff-line.model';
import { PortTariffLineFormModel, TariffLineKind } from '../../../shared/models/port-tariff-line-form.model';
import { NumberUtils } from '../../../shared/utils/number.utils';
import { RateBlockKey, RateBlockValues } from './rate-block.model';
import { RATE_BLOCK_FIELD_NAMES, RATE_BLOCK_KEYS, RATE_BLOCK_LABEL_KEYS } from './rate-block.constants';

export interface SelectOption {
  value: string;
  label: string;
}

// core-ui's SelectFieldComponent treats `null`/`''` as "no selection" (blank placeholder),
// so the "All cargo units" option can't use `null` as its option value or it will never
// render as selected. This sentinel stands in for null in the UI layer only — it's
// converted back to a real `null` at the model boundary (buildPayload/draftFromModel).
export const ALL_CARGO_TYPES_VALUE = '__all_cargo_types__';

// Same sentinel trick as ALL_CARGO_TYPES_VALUE, applied to the vessel selector:
// applicableVessel === null means "all vessels", but the select needs a real option value.
export const ALL_VESSELS_VALUE = '__all_vessels__';

@Injectable({
  providedIn: 'root',
})
export class PortTariffLineFormService {
  private readonly translateService = inject(TranslateService);

  createEmptyDraft(overrides: Partial<PortTariffLineFormModel> = {}): PortTariffLineFormModel {
    return new PortTariffLineFormModel().fromJSON({
      concept: '',
      sortOrder: 1,
      lineType: PortTariffLineTypes.STANDARD,
      rateValue: null,
      unitType: PortTariffLineUnitTypes.FIXED,
      vesselProperty: PortTariffLineVesselProperties.NONE,
      expenseCategory: PortTariffLineExpenseCategories.PORT,
      operationDirection: PortTariffLineOperationDirections.BOTH,
      cargoType: null,
      applicableVessel: null,
      quantityMultiplier: '1.0000',
      ot1RateValue: null,
      ot1UnitType: PortTariffLineUnitTypes.FIXED,
      ot1VesselProperty: PortTariffLineVesselProperties.NONE,
      ot1CargoType: null,
      ot1QuantityMultiplier: '1.0000',
      ot2RateValue: null,
      ot2UnitType: PortTariffLineUnitTypes.FIXED,
      ot2VesselProperty: PortTariffLineVesselProperties.NONE,
      ot2CargoType: null,
      ot2QuantityMultiplier: '1.0000',
      ...overrides,
    });
  }

  // Converts an API model into a form draft. Standard/OT1/OT2 are each read the same
  // way (see readRateBlock), then copied into the draft's ot1*/ot2* fields.
  draftFromModel(line: PortTariffLineModel): PortTariffLineFormModel {
    const lineType = this.normalizeLineType(line.lineType);
    const standard = this.readRateBlock(lineType, 'standard', line);
    const ot1 = this.readRateBlock(lineType, 'ot1', line);
    const ot2 = this.readRateBlock(lineType, 'ot2', line);

    return this.createEmptyDraft({
      concept: line.concept ?? '',
      sortOrder: Number(line.sortOrder ?? 1),
      lineType,
      expenseCategory: line.expenseCategory ?? PortTariffLineExpenseCategories.PORT,
      operationDirection: line.operationDirection ?? PortTariffLineOperationDirections.BOTH,
      applicableVessel: line.applicableVessel ?? null,

      rateValue: standard.rateValue,
      unitType: standard.unitType ?? PortTariffLineUnitTypes.FIXED,
      vesselProperty: standard.vesselProperty ?? PortTariffLineVesselProperties.NONE,
      quantityMultiplier: standard.quantityMultiplier ?? '1.0000',
      cargoType: standard.cargoType,

      ot1RateValue: ot1.rateValue,
      ot1UnitType: ot1.unitType ?? PortTariffLineUnitTypes.FIXED,
      ot1VesselProperty: ot1.vesselProperty ?? PortTariffLineVesselProperties.NONE,
      ot1QuantityMultiplier: ot1.quantityMultiplier ?? '1.0000',
      ot1CargoType: ot1.cargoType,

      ot2RateValue: ot2.rateValue,
      ot2UnitType: ot2.unitType ?? PortTariffLineUnitTypes.FIXED,
      ot2VesselProperty: ot2.vesselProperty ?? PortTariffLineVesselProperties.NONE,
      ot2QuantityMultiplier: ot2.quantityMultiplier ?? '1.0000',
      ot2CargoType: ot2.cargoType,
    });
  }

  // Builds the request body sent to the API. Standard/OT1/OT2 are each built the same
  // way (see buildRateBlockPayload): a block with no amount is sent as entirely null.
  buildPayload(draft: PortTariffLineFormModel): Partial<PortTariffLineModel> {
    const lineType = this.normalizeLineType(draft.lineType);
    const standard = this.buildRateBlockPayload(lineType, 'standard', draft);
    const ot1 = this.buildRateBlockPayload(lineType, 'ot1', draft);
    const ot2 = this.buildRateBlockPayload(lineType, 'ot2', draft);

    return {
      concept: String(draft.concept ?? '').trim(),
      sortOrder: Number(draft.sortOrder ?? 0),
      lineType,
      expenseCategory: draft.expenseCategory ?? PortTariffLineExpenseCategories.PORT,
      // Direction only matters for stevedoring lines. Any other category is
      // forced to BOTH regardless of what's in the draft, since the field is
      // hidden in the UI for those categories and BOTH means "whole scale".
      operationDirection: draft.expenseCategory === PortTariffLineExpenseCategories.STEVEDORING
        ? (draft.operationDirection ?? PortTariffLineOperationDirections.BOTH)
        : PortTariffLineOperationDirections.BOTH,
      applicableVessel: draft.applicableVessel ?? null,

      rateValue: standard.rateValue,
      unitType: standard.unitType,
      vesselProperty: standard.vesselProperty,
      quantityMultiplier: standard.quantityMultiplier,
      cargoType: standard.cargoType,

      ot1RateValue: ot1.rateValue,
      ot1UnitType: ot1.unitType,
      ot1VesselProperty: ot1.vesselProperty,
      ot1QuantityMultiplier: ot1.quantityMultiplier,
      ot1CargoType: ot1.cargoType,

      ot2RateValue: ot2.rateValue,
      ot2UnitType: ot2.unitType,
      ot2VesselProperty: ot2.vesselProperty,
      ot2QuantityMultiplier: ot2.quantityMultiplier,
      ot2CargoType: ot2.cargoType,
    };
  }

  // Validates the whole draft before it's sent to the API. Returns the first error
  // message found, or null if the draft is valid.
  validate(draft: PortTariffLineFormModel): string | null {
    const concept = String(draft.concept ?? '').trim();
    if (!concept) {
      return this.translate('portTariffLines.validation.conceptRequired');
    }

    if (concept.length > 255) {
      return this.translate('portTariffLines.validation.conceptMaxLength');
    }

    const sortOrder = Number(draft.sortOrder ?? 0);
    if (!Number.isInteger(sortOrder) || sortOrder <= 0) {
      return this.translate('portTariffLines.validation.sortOrderPositive');
    }

    // Check Standard, OT1 and OT2 one at a time. An empty block is always valid (it
    // just doesn't apply), so blockError() only reports a problem when a block has
    // an amount but that amount or its formula is invalid.
    let atLeastOneBlockHasARate = false;
    for (const block of RATE_BLOCK_KEYS) {
      const blockError = this.validateRateBlock(draft, block);
      if (blockError.message) {
        return blockError.message;
      }
      if (blockError.hasPositiveRate) {
        atLeastOneBlockHasARate = true;
      }
    }

    if (!atLeastOneBlockHasARate) {
      return this.translate('portTariffLines.validation.atLeastOneRateRequired');
    }

    return null;
  }

  expenseCategoryOptions(): SelectOption[] {
    return [
      { value: PortTariffLineExpenseCategories.PORT, label: this.translate('portTariffLines.options.expenseCategory.port') },
      { value: PortTariffLineExpenseCategories.STEVEDORING, label: this.translate('portTariffLines.options.expenseCategory.stevedoring') },
      { value: PortTariffLineExpenseCategories.AGENCY, label: this.translate('portTariffLines.options.expenseCategory.agency') },
    ];
  }

  operationDirectionOptions(): SelectOption[] {
    return [
      { value: PortTariffLineOperationDirections.LOADING, label: this.translate('portTariffLines.options.operationDirection.loading') },
      { value: PortTariffLineOperationDirections.DISCHARGE, label: this.translate('portTariffLines.options.operationDirection.discharge') },
      { value: PortTariffLineOperationDirections.BOTH, label: this.translate('portTariffLines.options.operationDirection.both') },
    ];
  }

  nextSortOrder(activeLines: PortTariffLineModel[]): number {
    const lastSortOrder = activeLines.at(-1)?.sortOrder ?? 0;
    return lastSortOrder + 1;
  }

  unitTypeOptions(lineType: TariffLineKind | string): SelectOption[] {
    if (lineType === PortTariffLineTypes.TAX) {
      return [
        { value: PortTariffLineUnitTypes.FIXED, label: this.translate('portTariffLines.options.unitType.fixed') },
        { value: PortTariffLineUnitTypes.PCT, label: this.translate('portTariffLines.formula.pctUnitLabel') },
      ];
    }

    return [
      { value: PortTariffLineUnitTypes.FIXED, label: this.translate('portTariffLines.options.unitType.fixed') },
      { value: PortTariffLineUnitTypes.DAY, label: this.translate('portTariffLines.options.unitType.day') },
      { value: PortTariffLineUnitTypes.HOUR, label: this.translate('portTariffLines.options.unitType.hour') },
      { value: PortTariffLineUnitTypes.METER, label: this.translate('portTariffLines.options.unitType.meter') },
      { value: PortTariffLineUnitTypes.TON, label: this.translate('portTariffLines.options.unitType.ton') },
      { value: PortTariffLineUnitTypes.CARGO, label: this.translate('portTariffLines.options.unitType.cargo') },
      { value: PortTariffLineUnitTypes.CARGO_CBM, label: this.translate('portTariffLines.options.unitType.cargoCbm') },
      { value: PortTariffLineUnitTypes.CARGO_MT, label: this.translate('portTariffLines.options.unitType.cargoMt') },
      { value: PortTariffLineUnitTypes.CARGO_WM, label: this.translate('portTariffLines.options.unitType.cargoWm') },
    ];
  }

  vesselPropertyOptions(unitType: PortTariffLineUnitTypes | string): SelectOption[] {
    // Cargo-basis units (per unit, CBM, MT, W/M) always price off the cargo measure —
    // there's no vessel property to multiply by, so vesselProperty is fixed to NONE.
    if (isCargoUnitType(unitType)) {
      return [{ value: PortTariffLineVesselProperties.NONE, label: this.translate('portTariffLines.options.vesselProperty.none') }];
    }

    if (unitType === PortTariffLineUnitTypes.METER) {
      return [
        { value: PortTariffLineVesselProperties.LOA, label: this.translate('portTariffLines.options.vesselProperty.loa') },
        { value: PortTariffLineVesselProperties.BEAM, label: this.translate('portTariffLines.options.vesselProperty.beam') },
      ];
    }

    if (unitType === PortTariffLineUnitTypes.TON) {
      return [
        { value: PortTariffLineVesselProperties.GRT, label: this.translate('portTariffLines.options.vesselProperty.grt') },
        { value: PortTariffLineVesselProperties.NRT, label: this.translate('portTariffLines.options.vesselProperty.nrt') },
        { value: PortTariffLineVesselProperties.DWT, label: this.translate('portTariffLines.options.vesselProperty.dwt') },
      ];
    }

    // All other standard unit types (DAY, HOUR, FIXED) support any vessel property
    // as an optional multiplier.
    return [
      { value: PortTariffLineVesselProperties.NONE, label: this.translate('portTariffLines.options.vesselProperty.none') },
      { value: PortTariffLineVesselProperties.LOA, label: this.translate('portTariffLines.options.vesselProperty.loa') },
      { value: PortTariffLineVesselProperties.BEAM, label: this.translate('portTariffLines.options.vesselProperty.beam') },
      { value: PortTariffLineVesselProperties.GRT, label: this.translate('portTariffLines.options.vesselProperty.grt') },
      { value: PortTariffLineVesselProperties.NRT, label: this.translate('portTariffLines.options.vesselProperty.nrt') },
      { value: PortTariffLineVesselProperties.DWT, label: this.translate('portTariffLines.options.vesselProperty.dwt') },
    ];
  }

  private normalizeLineType(lineType: string | undefined): TariffLineKind {
    return lineType === PortTariffLineTypes.TAX ? PortTariffLineTypes.TAX : PortTariffLineTypes.STANDARD;
  }

  // Reads one rate block (Standard/OT1/OT2) off an API model, converting its raw
  // decimal into the form's display representation (percentages shown as e.g. 50,
  // not 0.5). If the block has no rate, every field comes back null — an empty rate
  // is never presented as if it were priced at USD 0.
  private readRateBlock(lineType: TariffLineKind, block: RateBlockKey, line: PortTariffLineModel): RateBlockValues {
    const fieldNames = RATE_BLOCK_FIELD_NAMES[block];
    const rawRateValue = this.getField(line, fieldNames.rateValue);

    if (rawRateValue === null || rawRateValue === undefined || rawRateValue === '') {
      return { rateValue: null, unitType: null, vesselProperty: null, quantityMultiplier: null, cargoType: null };
    }

    const unitType = this.normalizeUnitType(lineType, this.getField(line, fieldNames.unitType));
    const isPercentage = this.isPercentageRate(lineType, unitType);
    const rateValue = isPercentage
      ? NumberUtils.toFixedDecimalString(Number(rawRateValue) * 100, 2)
      : NumberUtils.toFixedDecimalString(rawRateValue as string | number);

    return {
      rateValue,
      unitType,
      vesselProperty: this.normalizeVesselProperty(unitType, this.getField(line, fieldNames.vesselProperty)),
      quantityMultiplier: this.resolveQuantityMultiplier(lineType, this.getField(line, fieldNames.quantityMultiplier)),
      cargoType: this.resolveCargoType(unitType, this.getField(line, fieldNames.cargoType)),
    };
  }

  // Builds one rate block's API payload. A block with no amount is sent as entirely
  // null (rate + every formula field), so no leftover unitType/vesselProperty from a
  // previous edit reaches the backend and trips its "incomplete block" validation.
  private buildRateBlockPayload(lineType: TariffLineKind, block: RateBlockKey, draft: PortTariffLineFormModel): RateBlockValues {
    const fieldNames = RATE_BLOCK_FIELD_NAMES[block];
    const rawRateValue = String(this.getField(draft, fieldNames.rateValue) ?? '').trim();

    if (!rawRateValue) {
      return { rateValue: null, unitType: null, vesselProperty: null, quantityMultiplier: null, cargoType: null };
    }

    const unitType = this.normalizeUnitType(lineType, this.getField(draft, fieldNames.unitType));
    const isPercentage = this.isPercentageRate(lineType, unitType);
    const rateValue = isPercentage
      ? NumberUtils.toFixedDecimalString(Number(rawRateValue) / 100)
      : NumberUtils.toFixedDecimalString(rawRateValue);

    return {
      rateValue,
      unitType,
      vesselProperty: this.normalizeVesselProperty(unitType, this.getField(draft, fieldNames.vesselProperty)),
      quantityMultiplier: this.resolveQuantityMultiplier(lineType, this.getField(draft, fieldNames.quantityMultiplier)),
      cargoType: this.resolveCargoType(unitType, this.getField(draft, fieldNames.cargoType)),
    };
  }

  // Checks a single rate block: is its amount valid, and — only if it has an amount —
  // is the rest of its formula valid too? An empty block is always fine.
  private validateRateBlock(draft: PortTariffLineFormModel, block: RateBlockKey): { message: string | null; hasPositiveRate: boolean } {
    const fieldNames = RATE_BLOCK_FIELD_NAMES[block];
    const blockLabel = this.translate(RATE_BLOCK_LABEL_KEYS[block]);
    const rateValue = String(this.getField(draft, fieldNames.rateValue) ?? '').trim();

    if (!rateValue) {
      return { message: null, hasPositiveRate: false };
    }

    if (!this.isValidDecimal(rateValue)) {
      return { message: this.translate('portTariffLines.validation.rateValuePatternForBlock', { block: blockLabel }), hasPositiveRate: false };
    }

    const quantityMultiplier = String(this.getField(draft, fieldNames.quantityMultiplier) ?? '').trim();
    if (!this.isValidDecimal(quantityMultiplier) || Number(quantityMultiplier) <= 0) {
      return {
        message: this.translate('portTariffLines.validation.quantityMultiplierPatternForBlock', { block: blockLabel }),
        hasPositiveRate: false,
      };
    }

    const unitType = String(this.getField(draft, fieldNames.unitType) ?? '');
    const vesselProperty = String(this.getField(draft, fieldNames.vesselProperty) ?? '');
    const cargoType = this.getField(draft, fieldNames.cargoType);

    if (draft.lineType === PortTariffLineTypes.TAX && unitType !== PortTariffLineUnitTypes.FIXED && unitType !== PortTariffLineUnitTypes.PCT) {
      return { message: this.translate('portTariffLines.validation.taxUnitMismatch'), hasPositiveRate: false };
    }

    if (draft.lineType !== PortTariffLineTypes.TAX && unitType === PortTariffLineUnitTypes.PCT) {
      return { message: this.translate('portTariffLines.validation.pctOnlyForTax'), hasPositiveRate: false };
    }

    const vesselPropertyIsValid = this.isVesselPropertyValidForUnitType(unitType, vesselProperty);
    if (unitType === PortTariffLineUnitTypes.METER && !vesselPropertyIsValid) {
      return { message: this.translate('portTariffLines.validation.meterVesselProperty'), hasPositiveRate: false };
    }

    if (unitType === PortTariffLineUnitTypes.TON && !vesselPropertyIsValid) {
      return { message: this.translate('portTariffLines.validation.tonVesselProperty'), hasPositiveRate: false };
    }

    if (cargoType !== null && cargoType !== undefined && !isCargoUnitType(unitType)) {
      return { message: this.translate('portTariffLines.validation.cargoTypeOnlyForCargoUnit'), hasPositiveRate: false };
    }

    return { message: null, hasPositiveRate: Number(rateValue) > 0 };
  }

  private isVesselPropertyValidForUnitType(unitType: string, vesselProperty: string): boolean {
    if (unitType === PortTariffLineUnitTypes.METER) {
      return vesselProperty === PortTariffLineVesselProperties.LOA || vesselProperty === PortTariffLineVesselProperties.BEAM;
    }

    if (unitType === PortTariffLineUnitTypes.TON) {
      return (
        vesselProperty === PortTariffLineVesselProperties.GRT ||
        vesselProperty === PortTariffLineVesselProperties.NRT ||
        vesselProperty === PortTariffLineVesselProperties.DWT
      );
    }

    return true;
  }

  private isValidDecimal(value: string): boolean {
    return /^\d+(\.\d{1,4})?$/.test(value);
  }

  // Reads a named field off a model/draft object. The field name is picked at
  // runtime from RATE_BLOCK_FIELD_NAMES (e.g. 'ot1RateValue'), so this needs a
  // plain index lookup instead of direct property access (`line.ot1RateValue`).
  private getField(source: PortTariffLineModel | PortTariffLineFormModel, fieldName: string): unknown {
    return (source as unknown as Record<string, unknown>)[fieldName];
  }

  private isPercentageRate(lineType: TariffLineKind | string, unitType: PortTariffLineUnitTypes | string): boolean {
    return lineType === PortTariffLineTypes.TAX && unitType === PortTariffLineUnitTypes.PCT;
  }

  // Tax lines always multiply by 1 (their rate already IS the final amount), so the
  // multiplier is forced back to 1.0000 regardless of what was entered.
  private resolveQuantityMultiplier(lineType: TariffLineKind, quantityMultiplier: unknown): string {
    if (lineType === PortTariffLineTypes.TAX) {
      return '1.0000';
    }

    return NumberUtils.toFixedDecimalString((quantityMultiplier as string | undefined) ?? '1.0000');
  }

  // Cargo type only makes sense for "per cargo unit" lines — for every other unit
  // type it's cleared, even if a value happens to be present.
  private resolveCargoType(unitType: PortTariffLineUnitTypes, cargoType: unknown): string | null {
    return isCargoUnitType(unitType) ? ((cargoType as string | null | undefined) ?? null) : null;
  }

  private normalizeUnitType(
    lineType: TariffLineKind | string | undefined,
    unitType: unknown
  ): PortTariffLineUnitTypes {
    if (lineType === PortTariffLineTypes.TAX) {
      return unitType === PortTariffLineUnitTypes.FIXED ? PortTariffLineUnitTypes.FIXED : PortTariffLineUnitTypes.PCT;
    }

    if (unitType === PortTariffLineUnitTypes.PCT) {
      return PortTariffLineUnitTypes.FIXED;
    }

    return (unitType as PortTariffLineUnitTypes) ?? PortTariffLineUnitTypes.FIXED;
  }

  private normalizeVesselProperty(
    unitType: PortTariffLineUnitTypes | string | undefined,
    vesselProperty: unknown
  ): PortTariffLineVesselProperties {
    if (unitType === PortTariffLineUnitTypes.METER) {
      return vesselProperty === PortTariffLineVesselProperties.BEAM
        ? PortTariffLineVesselProperties.BEAM
        : PortTariffLineVesselProperties.LOA;
    }

    if (unitType === PortTariffLineUnitTypes.TON) {
      if (vesselProperty === PortTariffLineVesselProperties.NRT || vesselProperty === PortTariffLineVesselProperties.DWT) {
        return vesselProperty;
      }

      return PortTariffLineVesselProperties.GRT;
    }

    if (isCargoUnitType(unitType)) {
      return PortTariffLineVesselProperties.NONE;
    }

    if (
      vesselProperty === PortTariffLineVesselProperties.LOA ||
      vesselProperty === PortTariffLineVesselProperties.BEAM ||
      vesselProperty === PortTariffLineVesselProperties.GRT ||
      vesselProperty === PortTariffLineVesselProperties.NRT ||
      vesselProperty === PortTariffLineVesselProperties.DWT
    ) {
      return vesselProperty;
    }

    return PortTariffLineVesselProperties.NONE;
  }

  private translate(key: string, params?: Record<string, unknown>): string {
    return this.translateService.instant(key, params);
  }
}
